import { Finding } from '../../../types';
import { createAuthenticatedApiClient } from '../../../shared/api/apiClient';
import { rpcCall } from '../../../shared/api/rpcClient';
import { useAuth } from '../../auth/context/AuthContext';
import { useMemo } from 'react';
import { getInitialRank, assignRanks } from '../../../shared/utils/ranking';

export const createFindingsService = (idToken: string) => {
  const client = createAuthenticatedApiClient(idToken);

  const getFindingsForInvestigation = async (customerId: string, investigationId: string): Promise<Finding[]> => {
    const result = await rpcCall<{ findings: Finding[] }>(client, 'investigations.GetFindings', { customer_id: customerId, investigation_id: investigationId });
    return result.findings;
  };

  const updateFindingRanks = async (
    customerId: string,
    investigationId: string,
    findings: Finding[]
  ): Promise<Finding[]> => {
    const result = await rpcCall<{ findings: Finding[] }>(client, 'investigations.UpdateFindingRanks', { customer_id: customerId, investigation_id: investigationId, findings });
    return result.findings;
  };

  const addFinding = async (
    customerId: string,
    investigationId: string,
    finding: Omit<Finding, 'id' | 'rank'>
  ): Promise<Finding[]> => {
    await rpcCall(client, 'investigations.AddFinding', {
      customer_id: customerId,
      investigation_id: investigationId,
      title: finding.title,
      description: finding.description,
      rank: getInitialRank(),
      irrelevant: false,
      potential_root_cause: false,
    });
    return getFindingsForInvestigation(customerId, investigationId);
  };

  const updateFinding = async (
    customerId: string,
    investigationId: string,
    findingId: string,
    updates: Partial<Finding>
  ): Promise<Finding[]> => {
    await rpcCall(client, 'investigations.UpdateFinding', {
      customer_id: customerId,
      investigation_id: investigationId,
      finding_id: findingId,
      ...updates,
    });
    return getFindingsForInvestigation(customerId, investigationId);
  };

  const removeFinding = async (customerId: string, investigationId: string, findingId: string): Promise<Finding[]> => {
    await rpcCall(client, 'investigations.DeleteFindings', {
      customer_id: customerId,
      investigation_id: investigationId,
      finding_ids: [findingId],
    });
    const findings = await getFindingsForInvestigation(customerId, investigationId);
    const relevantFindings = findings.filter(f => !f.irrelevant);
    const irrelevantFindings = findings.filter(f => f.irrelevant);
    const reordered = [...relevantFindings, ...irrelevantFindings];
    return updateFindingRanking(customerId, investigationId, reordered);
  };

  const updateFindingRanking = async (
    customerId: string,
    investigationId: string,
    findings: Finding[]
  ): Promise<Finding[]> => {
    const rerankedFindings = assignRanks(findings);
    return updateFindingRanks(customerId, investigationId, rerankedFindings);
  };

  const toggleFindingRelevance = async (
    customerId: string,
    investigationId: string,
    findingId: string,
    irrelevant: boolean
  ): Promise<Finding[]> => {
    await updateFinding(customerId, investigationId, findingId, { irrelevant });

    const findings = await getFindingsForInvestigation(customerId, investigationId);
    const relevantFindings = findings.filter(f => !f.irrelevant);
    const irrelevantFindings = findings.filter(f => f.irrelevant);
    const reordered = [...relevantFindings, ...irrelevantFindings];

    return updateFindingRanking(customerId, investigationId, reordered);
  };

  const togglePotentialRootCause = async (
    customerId: string,
    investigationId: string,
    findingId: string,
    isPotentialRootCause: boolean
  ): Promise<Finding[]> => {
    return updateFinding(customerId, investigationId, findingId, {
      potential_root_cause: isPotentialRootCause
    });
  };

  return {
    getFindingsForInvestigation,
    updateFindingRanks,
    addFinding,
    updateFinding,
    removeFinding,
    updateFindingRanking,
    toggleFindingRelevance,
    togglePotentialRootCause,
  };
};

export const useFindingsService = () => {
  const { idToken } = useAuth();

  return useMemo(() => {
    if (!idToken) {
      throw new Error('Not authenticated');
    }

    return createFindingsService(idToken);
  }, [idToken]);
};
